// [claude-code 2026-05-03] S58-T2: DeepSeek API-key settings section for personal CAO chat.
import { useCallback, useEffect, useState } from "react";
import { getAccessToken } from "../../lib/supabase";
import { SettingsActionStatus } from "./SettingsActionStatus";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:8080";

type ProviderId = "deepseek" | "opencode-go";

const PROVIDERS: Array<{
  helpHref: string;
  helpLabel: string;
  id: ProviderId;
  label: string;
  placeholder: string;
  storageKey: string;
}> = [
  {
    helpHref: "https://platform.deepseek.com/api_keys",
    helpLabel: "platform.deepseek.com/api_keys",
    id: "deepseek",
    label: "DeepSeek",
    placeholder: "sk-...",
    storageKey: "fintheon:deepseek-key-status",
  },
  {
    helpHref: "https://opencode.ai",
    helpLabel: "opencode.ai",
    id: "opencode-go",
    label: "OpenCode Go",
    placeholder: "API key",
    storageKey: "fintheon:opencode-go-key-status",
  },
];

function emptyProviderRecord(): Record<ProviderId, string> {
  return { deepseek: "", "opencode-go": "" };
}

export function DeepSeekApiKeySection() {
  const [keyInputs, setKeyInputs] =
    useState<Record<ProviderId, string>>(emptyProviderRecord);
  const [maskedKeys, setMaskedKeys] = useState<
    Partial<Record<ProviderId, string | null>>
  >({});
  const [isSavingProvider, setIsSavingProvider] = useState<ProviderId | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const aiKeyHeaders = useCallback(async () => {
    const token = await getAccessToken();
    return {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  }, []);

  const fetchProviderStatus = useCallback(
    async (provider: ProviderId) => {
      const config = PROVIDERS.find((item) => item.id === provider)!;
      try {
        const res = await fetch(
          `${API_BASE}/api/settings/ai-keys?provider=${encodeURIComponent(provider)}`,
          {
            headers: await aiKeyHeaders(),
            credentials: "include",
          },
        );
        if (!res.ok) return;
        const data = await res.json();
        const masked =
          data.maskedKey ??
          data.masked ??
          data.keys?.[0]?.maskedKey ??
          data.keys?.[0]?.masked ??
          null;
        setMaskedKeys((prev) => ({ ...prev, [provider]: masked }));
        localStorage.setItem(config.storageKey, masked ? "set" : "missing");
      } catch {
        localStorage.setItem(config.storageKey, "missing");
      }
    },
    [aiKeyHeaders],
  );

  useEffect(() => {
    for (const provider of PROVIDERS) {
      void fetchProviderStatus(provider.id);
    }
  }, [fetchProviderStatus]);

  const addKey = async (provider: ProviderId, apiKey: string) => {
    const config = PROVIDERS.find((item) => item.id === provider)!;
    if (!apiKey || apiKey.length < 10) {
      setError("API key must be at least 10 characters");
      return;
    }
    setIsSavingProvider(provider);
    setError(null);
    setSuccess(null);
    try {
      const res = await fetch(`${API_BASE}/api/settings/ai-keys`, {
        method: "POST",
        headers: await aiKeyHeaders(),
        credentials: "include",
        body: JSON.stringify({ provider, apiKey }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || data.message || "Failed to add API key");
      } else {
        const backendSaved = data.backendSaved !== false;
        const localHermesSaved = data.localHermesSaved === true;
        setSuccess(
          `${config.label} key saved${backendSaved ? " to backend" : ""}${localHermesSaved ? " and local Hermes" : ""}.`,
        );
        setKeyInputs((prev) => ({ ...prev, [provider]: "" }));
        localStorage.setItem(config.storageKey, "set");
        if (provider === "opencode-go") {
          localStorage.setItem("fintheon:harper-provider", "opencode-go");
          localStorage.setItem("fintheon:default-chat-provider", "opencode-go");
        }
        void fetchProviderStatus(provider);
      }
    } catch {
      setError("Network error");
    } finally {
      setIsSavingProvider(null);
    }
  };

  const removeKey = async (provider: ProviderId) => {
    const config = PROVIDERS.find((item) => item.id === provider)!;
    setError(null);
    setSuccess(null);
    try {
      const res = await fetch(
        `${API_BASE}/api/settings/ai-keys?provider=${encodeURIComponent(provider)}`,
        {
          method: "DELETE",
          headers: await aiKeyHeaders(),
          credentials: "include",
        },
      );
      if (res.ok) {
        setSuccess(`${config.label} key removed.`);
        setMaskedKeys((prev) => ({ ...prev, [provider]: null }));
        localStorage.setItem(config.storageKey, "missing");
      }
    } catch {
      setError("Failed to remove API key");
    }
  };

  return (
    <section className="text-right">
      <h3 className="mb-1 text-right text-sm font-semibold text-[var(--fintheon-accent)]">
        AI Provider API Keys
      </h3>
      <p className="mb-4 text-right text-xs text-gray-500">
        Save account-level DeepSeek or OpenCode Go keys for CAO fallback. Grok
        connects through Hermes.
      </p>

      {PROVIDERS.map((provider) => {
        const masked = maskedKeys[provider.id] ?? null;
        const inputValue = keyInputs[provider.id];
        return (
          <div className="fintheon-fade-divider mb-4 pb-3" key={provider.id}>
            <div className="mb-3 flex items-start justify-between gap-4 text-right">
              <div className="min-w-0 text-right">
                <p className="text-xs font-medium text-gray-300">
                  {provider.label}
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  Create a key at{" "}
                  <a
                    href={provider.helpHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[var(--fintheon-accent)] hover:underline"
                  >
                    {provider.helpLabel}
                  </a>
                  .
                </p>
              </div>
              <div className="flex min-w-[180px] flex-col items-end gap-1 text-right">
                <SettingsActionStatus
                  label={masked ? "Key Set" : "No Key"}
                  detail={masked ?? "No key configured"}
                  tone={masked ? "success" : "muted"}
                />
                {masked && (
                  <button
                    onClick={() => removeKey(provider.id)}
                    className="text-[10px] uppercase tracking-[0.14em] text-red-400 transition-colors hover:text-red-300"
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>

            <div className="flex gap-2">
              <input
                type="password"
                value={inputValue}
                onChange={(event) => {
                  setKeyInputs((prev) => ({
                    ...prev,
                    [provider.id]: event.target.value,
                  }));
                  setError(null);
                  setSuccess(null);
                }}
                placeholder={provider.placeholder}
                className="flex-1 rounded border border-zinc-800 bg-[var(--fintheon-surface)] px-3 py-2 text-sm text-white placeholder:text-gray-600 focus:border-[var(--fintheon-accent)]/30 focus:outline-none"
              />
              <button
                onClick={() => addKey(provider.id, inputValue)}
                disabled={isSavingProvider === provider.id || !inputValue}
                className="rounded border border-[var(--fintheon-accent)]/30 bg-[var(--fintheon-accent)]/20 px-4 py-2 text-sm text-[var(--fintheon-accent)] transition-colors hover:bg-[var(--fintheon-accent)]/30 disabled:opacity-40"
              >
                {isSavingProvider === provider.id ? "..." : "Add Key"}
              </button>
            </div>
          </div>
        );
      })}

      <div className="mt-2 flex justify-end">
        {error && (
          <SettingsActionStatus label="Key Error" detail={error} tone="error" />
        )}
        {success && (
          <SettingsActionStatus
            label="Key Saved"
            detail={success}
            tone="success"
          />
        )}
      </div>
    </section>
  );
}
